import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Alert,
  Grid,
  Collapse,
  IconButton,
  Paper,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  LocalHospital as StudyIcon,
  Folder as DocumentsIcon,
  CloudUpload as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import apiClient from '../services/api'
import { PatientWithHistory, TimelineEntry, Document } from '../types/api'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'

// Componente refactorizate
import {
  PatientHeroCard,
  TimelineSection,
  QuickStatsPanel,
  PatientDocumentsSection
} from '../components/patients/details'
import { RemoteStudy } from '../components/patients/details/TimelineSection'

// Dialog-uri existente
import ReportViewerDialog from '../components/reports/ReportViewerDialog'
import DocumentViewerDialog from '../components/documents/DocumentViewerDialog'
import SimpleDocumentUploadDialog from '../components/documents/SimpleDocumentUploadDialog'

/**
 * PatientDetailsPage - Pagina de detalii pacient refactorizată v2
 * 
 * Layout nou:
 * 1. Header: Breadcrumbs + Actions (Încarcă Examen, Încarcă Document, Refresh, Înapoi)
 * 2. PatientHeroCard (info + stats)
 * 3. QuickActionsBar (badges clickabile + acțiuni rapide)
 * 4. Secțiuni colapsabile: Timeline + Documente (vizibile simultan)
 * 5. Sidebar: QuickStatsPanel (statistici, tracking, rapoarte)
 */
const PatientDetailsPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { designSystem } = theme
  const colors = isDark ? designSystem.colors.dark : designSystem.colors.light
  
  // Refs pentru scroll
  const timelineRef = useRef<HTMLDivElement>(null)
  const documentsRef = useRef<HTMLDivElement>(null)
  
  // State principal
  const [patient, setPatient] = useState<PatientWithHistory | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // State pentru documente
  const [documents, setDocuments] = useState<Document[]>([])
  const [loadingDocuments, setLoadingDocuments] = useState(false)
  
  // State pentru rapoarte
  const [patientReports, setPatientReports] = useState<any[]>([])
  const [loadingReports, setLoadingReports] = useState(false)
  
  // State pentru studii remote
  const [remoteStudies, setRemoteStudies] = useState<RemoteStudy[]>([])
  const [loadingRemote, setLoadingRemote] = useState(false)
  const [pullingStudyId, setPullingStudyId] = useState<string | null>(null)
  
  // Ref pentru a tracking-ui ultimul CNP și preveni loop-uri
  const lastCnpRef = useRef<string | null>(null)
  
  // State pentru dialog-uri
  const [showUploadDocument, setShowUploadDocument] = useState(false)
  const [viewingReport, setViewingReport] = useState<{
    id: string
    status: 'draft' | 'final'
  } | null>(null)
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null)
  const [documentBlobUrl, setDocumentBlobUrl] = useState<string | null>(null)
  
  // State pentru secțiuni colapsabile
  const [timelineExpanded, setTimelineExpanded] = useState(true)
  const [documentsExpanded, setDocumentsExpanded] = useState(true)
  
  // State pentru editare pacient
  const [editOpen, setEditOpen] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    cnp: '',
    dob: '',
    sex: '',
    phone: '',
    email: ''
  })

  // ============================================================================
  // DATA LOADING
  // ============================================================================
  
  useEffect(() => {
    if (patientId) {
      loadPatientDetails()
      loadPatientDocuments()
    }
  }, [patientId])

  // Încarcă studii remote din patient_study_index
  useEffect(() => {
    if (patient?.cnp && patient.cnp !== lastCnpRef.current) {
      loadRemoteStudies().then(() => {
        lastCnpRef.current = patient.cnp
      })
    } else if (!patient?.cnp) {
      lastCnpRef.current = null
      setRemoteStudies([])
    }
  }, [patient?.cnp])

  // Încarcă rapoartele după ce pacientul este încărcat
  useEffect(() => {
    if (patient && patient.timeline && patient.timeline.length > 0) {
      loadPatientReports(patient)
    }
  }, [patient?.id, patient?.timeline?.length])

  const loadPatientDetails = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.get(`/patients/${patientId}`)
      if (response.status === 200) {
        setPatient(response.data)
      } else {
        throw new Error('Eroare la încărcarea detaliilor pacientului')
      }
    } catch (err: any) {
      console.error('Eroare la încărcarea pacientului:', err)
      const errorMsg = err.response?.data?.detail || 'Eroare la încărcarea detaliilor pacientului'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const loadPatientDocuments = async () => {
    try {
      setLoadingDocuments(true)
      const response = await apiClient.get(`/patients/${patientId}/documents?size=100`)
      if (response.status === 200) {
        setDocuments(response.data.documents || [])
      }
    } catch (err: any) {
      console.error('Eroare la încărcarea documentelor:', err)
    } finally {
      setLoadingDocuments(false)
    }
  }

  const loadPatientReports = async (currentPatient: PatientWithHistory) => {
    if (!patientId || !currentPatient) return
    
    try {
      setLoadingReports(true)
      const studies = currentPatient.timeline?.filter(e => e.type === 'study') || []
      
      if (studies.length > 0) {
        const studyIds = studies.map(s => s.id).filter(Boolean) as string[]
        
        const allReports: any[] = []
        for (const studyId of studyIds) {
          try {
            // Trailing slash to avoid 307 redirect (which strips Authorization header on mobile)
            const response = await apiClient.get(`/reports/?studyId=${studyId}&size=100`)
            if (response.status === 200 && response.data.reports) {
              allReports.push(...response.data.reports)
            }
          } catch (e) {
            console.error('Error loading reports for study:', studyId, e)
          }
        }
        const uniqueReports = allReports.filter((report, index, self) =>
          index === self.findIndex(r => r.id === report.id)
        )
        setPatientReports(uniqueReports)
      } else {
        setPatientReports([])
      }
    } catch (err) {
      console.error('Error loading patient reports:', err)
    } finally {
      setLoadingReports(false)
    }
  }

  const loadRemoteStudies = async () => {
    if (!patient?.cnp) {
      setRemoteStudies([])
      setLoadingRemote(false)
      return
    }
    
    try {
      setLoadingRemote(true)
      const response = await apiClient.get(`/patients/${patientId}/remote-studies`)
      if (response.status === 200) {
        setRemoteStudies(response.data.studies || [])
      }
    } catch (err: any) {
      console.error('Eroare la încărcarea studiilor remote:', err)
      setRemoteStudies([])
    } finally {
      setLoadingRemote(false)
    }
  }

  // On-demand download studiu din VuePACS Affidea
  const handlePullStudy = async (study: RemoteStudy) => {
    if (!patient?.cnp) {
      toast.error('CNP pacient lipsă')
      return
    }
    
    try {
      setPullingStudyId(study.id)
      toast.loading('Se descarcă studiul din VuePACS...', { id: 'pull-study' })
      
      const response = await apiClient.post('/studies/remote/pull', null, {
        params: {
          patient_cnp: patient.cnp,
          study_uid: study.study_instance_uid,
        },
        timeout: 10000,
      })
      
      toast.dismiss('pull-study')
      
      if (response.data.studies_transferred > 0) {
        toast.success(`Studiul a fost descărcat cu succes!`, { duration: 3000 })
        await loadPatientDetails()
      } else {
        toast.info('Download-ul studiului a fost inițiat și rulează în background', { duration: 5000 })
        setTimeout(() => loadPatientDetails(), 30000)
      }
    } catch (err: any) {
      toast.dismiss('pull-study')
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.info('Download-ul rulează în background. Verificați peste câteva minute.', { duration: 6000 })
        setPullingStudyId(null)
        return
      }
      
      const errorMsg = err.response?.data?.detail || 'Eroare la descărcarea studiului'
      toast.error(errorMsg, { duration: 4000 })
      console.error('Eroare pull study:', err)
    } finally {
      setPullingStudyId(null)
    }
  }

  // ============================================================================
  // HANDLERS
  // ============================================================================

  // Handler pentru deschiderea dialogului de editare
  const handleEditPatient = () => {
    if (!patient) return
    setEditData({
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      cnp: patient.cnp || '',
      dob: patient.dob ? new Date(patient.dob).toISOString().split('T')[0] : '',
      sex: patient.sex || '',
      phone: patient.phone || '',
      email: patient.email || ''
    })
    setEditError(null)
    setEditOpen(true)
  }

  // Handler pentru salvarea editării
  const handleEditSave = async () => {
    if (!patient) return

    try {
      setEditLoading(true)
      setEditError(null)

      // Validări
      if (editData.cnp && editData.cnp.length !== 13) {
        setEditError('CNP-ul trebuie să aibă exact 13 cifre')
        return
      }

      if (editData.sex && !['M', 'F', 'U'].includes(editData.sex)) {
        setEditError('Sexul trebuie să fie M, F sau U')
        return
      }

      const updateData = {
        first_name: editData.first_name.trim(),
        last_name: editData.last_name.trim(),
        cnp: editData.cnp.trim() || null,
        dob: editData.dob || null,
        sex: editData.sex || null,
        phone: editData.phone.trim() || null,
        email: editData.email.trim() || null
      }

      await apiClient.patch(`/patients/${patient.id}`, updateData)
      
      setEditOpen(false)
      toast.success('Pacient actualizat cu succes')
      loadPatientDetails() // Reîncarcă datele
    } catch (err: any) {
      console.error('Eroare la actualizarea pacientului:', err)
      setEditError('Eroare la actualizarea pacientului: ' + (err.response?.data?.detail || err.message))
    } finally {
      setEditLoading(false)
    }
  }

  // Handler pentru anularea editării
  const handleEditCancel = () => {
    setEditOpen(false)
    setEditError(null)
  }

  const handleScrollToTimeline = () => {
    setTimelineExpanded(true)
    setTimeout(() => {
      timelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const handleScrollToDocuments = () => {
    setDocumentsExpanded(true)
    setTimeout(() => {
      documentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const handleViewDocument = async (document: Document) => {
    try {
      toast.loading(`Se încarcă ${document.file_name}...`, { id: 'loading-doc' })
      
      const response = await apiClient.get(`/documents/${document.id}/download`, {
        responseType: 'blob'
      })
      
      const blob = new Blob([response.data], { type: document.mime })
      const blobUrl = URL.createObjectURL(blob)
      
      setDocumentBlobUrl(blobUrl)
      setViewingDocument(document)
      
      toast.success(`Document încărcat`, { id: 'loading-doc' })
    } catch (err: any) {
      console.error('Eroare la deschiderea documentului:', err)
      const errorMsg = err.response?.data?.detail || 'Nu s-a putut deschide documentul'
      toast.error(errorMsg, { id: 'loading-doc' })
    }
  }

  const handleCloseDocumentDialog = () => {
    if (documentBlobUrl) {
      URL.revokeObjectURL(documentBlobUrl)
    }
    setViewingDocument(null)
    setDocumentBlobUrl(null)
  }

  const handleViewReport = (entry: TimelineEntry | { id: string; status: 'draft' | 'final' }) => {
    const reportId = 'report_id' in entry ? entry.report_id : entry.id
    const reportStatus = entry.status as 'draft' | 'final'
    
    if (reportId && reportStatus) {
      setViewingReport({
        id: reportId,
        status: reportStatus
      })
    }
  }

  // ============================================================================
  // RENDER STATES
  // ============================================================================

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <PatientHeroCard patient={null} loading={true} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/patients')}
          variant="outlined"
        >
          Înapoi la Pacienți
        </Button>
      </Box>
    )
  }

  if (!patient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>Pacientul nu a fost găsit</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/patients')}
          variant="outlined"
        >
          Înapoi la Pacienți
        </Button>
      </Box>
    )
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  const studiesCount = patient.timeline?.filter(e => e.type === 'study').length || 0

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: colors.background.default,
    }}>
      {/* Hero Card Section - RESPONSIVE */}
      <Box
        sx={{
          backgroundColor: colors.background.paper,
          borderBottom: `1px solid ${colors.border.default}`,
          px: { xs: 1, sm: 2 },
          py: { xs: 0.75, sm: 1 },
        }}
      >
        <PatientHeroCard 
          patient={patient}
          onStudiesClick={handleScrollToTimeline}
          onDocumentsClick={handleScrollToDocuments}
          actionButtons={
            <>
              {/* Back button - hidden on mobile */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate(-1)}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  height: 28,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderColor: colors.border.default,
                  color: colors.text.secondary,
                  '&:hover': {
                    borderColor: colors.accent.primary,
                    color: colors.accent.primary,
                  }
                }}
              >
                Înapoi
              </Button>
              {/* Edit button - icon only on mobile */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={handleEditPatient}
                sx={{
                  height: { xs: 28, sm: 28 },
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderColor: colors.border.default,
                  color: colors.text.secondary,
                  minWidth: { xs: 28, sm: 'auto' },
                  px: { xs: 0.75, sm: 1.5 },
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0, sm: 0.5 },
                  },
                  '&:hover': {
                    borderColor: colors.accent.primary,
                    color: colors.accent.primary,
                  }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Editează</Box>
              </Button>
              {/* Upload button - icon only on mobile */}
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
                onClick={() => setShowUploadDocument(true)}
                sx={{
                  height: { xs: 28, sm: 28 },
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  boxShadow: 'none',
                  minWidth: { xs: 28, sm: 'auto' },
                  px: { xs: 0.75, sm: 1.5 },
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0, sm: 0.5 },
                  },
                  '&:hover': {
                    boxShadow: 'none',
                  }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Încarcă Document</Box>
              </Button>
            </>
          }
        />
      </Box>

      {/* Content Section - Two Columns - RESPONSIVE */}
      <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {/* Main Content - Collapsible Sections */}
          <Grid item xs={12} lg={8}>
            {/* Timeline Section - Collapsible */}
            <Box ref={timelineRef} sx={{ mb: 1.5 }}>
              <CollapsibleSection
                title="Istoric Medical Imagistic"
                icon={<StudyIcon />}
                count={studiesCount}
                expanded={timelineExpanded}
                onToggle={() => setTimelineExpanded(!timelineExpanded)}
                colors={colors}
                designSystem={designSystem}
              >
                <TimelineSection
                  timeline={patient.timeline}
                  onViewReport={handleViewReport}
                  remoteStudies={remoteStudies}
                  loadingRemote={loadingRemote}
                  onPullStudy={handlePullStudy}
                  pullingStudyId={pullingStudyId}
                />
              </CollapsibleSection>
            </Box>

            {/* Documents Section - Collapsible */}
            <Box ref={documentsRef}>
              <CollapsibleSection
                title="Documente"
                icon={<DocumentsIcon />}
                count={documents.length}
                expanded={documentsExpanded}
                onToggle={() => setDocumentsExpanded(!documentsExpanded)}
                colors={colors}
                designSystem={designSystem}
                accentColor={designSystem.colors.status.info}
              >
                <PatientDocumentsSection
                  documents={documents}
                  loading={loadingDocuments}
                  onUpload={() => setShowUploadDocument(true)}
                  onViewDocument={handleViewDocument}
                  onDocumentVerified={loadPatientDocuments}
                  compact
                />
              </CollapsibleSection>
            </Box>
          </Grid>

          {/* Sidebar - Quick Stats */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <QuickStatsPanel
                patient={patient}
                reports={patientReports}
                loadingReports={loadingReports}
                onViewReport={handleViewReport}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ========== DIALOGS ========== */}

      {/* Report Viewer Dialog */}
      <ReportViewerDialog
        open={!!viewingReport}
        reportId={viewingReport?.id || null}
        reportStatus={viewingReport?.status || 'final'}
        onClose={() => setViewingReport(null)}
      />

      {/* Simple Document Upload Dialog - NOU! */}
      {patient && (
        <SimpleDocumentUploadDialog
          open={showUploadDocument}
          onClose={() => setShowUploadDocument(false)}
          onSuccess={() => {
            loadPatientDocuments()
            setShowUploadDocument(false)
          }}
          patientId={patientId || ''}
          patientName={`${patient.first_name} ${patient.last_name}`}
        />
      )}

      {/* Document Viewer Dialog */}
      <DocumentViewerDialog
        open={!!viewingDocument}
        document={viewingDocument}
        blobUrl={documentBlobUrl}
        onClose={handleCloseDocumentDialog}
      />

      {/* Dialog editare pacient */}
      <Dialog
        open={editOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleEditCancel();
          }
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              fontSize: '0.85rem',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'primary.main',
            },
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiSelect-select': {
              color: 'text.primary',
            },
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
            },
          }
        }}
      >
        <DialogTitle sx={{ 
          py: 1.5, 
          px: 2,
          fontSize: '1rem',
          fontWeight: 600,
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon sx={{ fontSize: '1.1rem' }} />
            Editează Pacient
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {editError && (
            <Alert severity="error" sx={{ 
              mb: 1.5,
              py: 0.5,
              '& .MuiAlert-message': {
                fontSize: '0.8rem'
              }
            }}>
              {editError}
            </Alert>
          )}
          
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Date Identificare
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Prenume"
                value={editData.first_name}
                onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Nume"
                value={editData.last_name}
                onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="CNP"
                value={editData.cnp}
                onChange={(e) => setEditData({ ...editData, cnp: e.target.value })}
                helperText="13 cifre (opțional)"
                inputProps={{ maxLength: 13 }}
                FormHelperTextProps={{ sx: { fontSize: '0.7rem' } }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Data nașterii"
                type="date"
                value={editData.dob}
                onChange={(e) => setEditData({ ...editData, dob: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Sex</InputLabel>
                <Select
                  value={editData.sex}
                  label="Sex"
                  onChange={(e) => setEditData({ ...editData, sex: e.target.value })}
                >
                  <MenuItem value="">Necunoscut</MenuItem>
                  <MenuItem value="M">Masculin</MenuItem>
                  <MenuItem value="F">Feminin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Telefon"
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                helperText="Format: 07xxxxxxxx"
                FormHelperTextProps={{ sx: { fontSize: '0.7rem' } }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                helperText="Adresa de email (opțional)"
                FormHelperTextProps={{ sx: { fontSize: '0.7rem' } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1
        }}>
          <Button 
            onClick={handleEditCancel}
            startIcon={<CancelIcon sx={{ fontSize: '1rem' }} />}
            disabled={editLoading}
            size="small"
            sx={{ 
              fontSize: '0.8rem',
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary'
              }
            }}
          >
            Anulează
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            startIcon={<SaveIcon sx={{ fontSize: '1rem' }} />}
            disabled={editLoading || !editData.first_name.trim() || !editData.last_name.trim()}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            {editLoading ? 'Salvează...' : 'Salvează'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface CollapsibleSectionProps {
  title: string
  icon: React.ReactNode
  count: number
  expanded: boolean
  onToggle: () => void
  colors: any
  designSystem: any
  accentColor?: string
  children: React.ReactNode
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  count,
  expanded,
  onToggle,
  colors,
  designSystem,
  accentColor,
  children
}) => {
  const color = accentColor || colors.accent.primary

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: colors.background.paper,
        border: `1px solid ${colors.border.default}`,
        borderRadius: `${designSystem.components.card.borderRadius}px`,
        overflow: 'hidden',
      }}
    >
      {/* Header - RESPONSIVE */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.75, sm: 1 },
          cursor: 'pointer',
          backgroundColor: expanded ? colors.background.elevated : colors.background.paper,
          borderBottom: expanded ? `1px solid ${colors.border.default}` : 'none',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: colors.background.elevated,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
          <Box sx={{ color: color, display: 'flex', alignItems: 'center', '& svg': { fontSize: { xs: 16, sm: 18 } } }}>
            {icon}
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              backgroundColor: alpha(color, 0.15),
              color: color,
              px: 0.75,
              py: 0.125,
              borderRadius: 0.75,
              fontSize: '0.7rem',
              fontWeight: 600,
            }}
          >
            {count}
          </Box>
        </Box>
        <IconButton size="small" sx={{ color: colors.text.secondary, p: 0.5 }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 1.5 }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default PatientDetailsPage
